import { useState, useRef } from "react";
import { useForm, Controller } from "react-hook-form";
import { useLocation, Link } from "wouter";
import { motion, AnimatePresence } from "framer-motion";
import {
  User, MapPin, ChevronRight, ChevronLeft, Check, ChevronDown,
  Building2, Phone, ShieldCheck, Heart, CalendarIcon
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { format } from "date-fns";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { useGetLanguages, useGetLocalities, API_BASE } from "@/lib/api-client-react";

/* ─── Constants ─────────────────────────────────────────────────────────── */

const GENDERS = ["Male", "Female", "Non-binary", "Prefer not to say"];

const US_STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA",
  "KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ",
  "NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT",
  "VA","WA","WV","WI","WY","DC",
];

/* ─── Types ──────────────────────────────────────────────────────────────── */

type FormData = {
  firstName: string; middleName: string; lastName: string;
  displayName: string; ssn: string; gender: string; dob: string;
  languages: string[]; localityIds: string[]; email: string;
  buildingNo: string; street1: string; street2: string;
  city: string; state: string; zipCode: string;
  phone: string; whatsapp: string;
};

/* ─── MultiSelect ────────────────────────────────────────────────────────── */

function MultiSelect({
  options = [], value = [], onChange, placeholder, maxHeight = 260,
}: {
  options: any[]; value: string[]; onChange: (v: string[]) => void;
  placeholder: string; maxHeight?: number;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const normalizedOptions = (options || []).map((o: any) => {
    if (typeof o === "string") return { value: o, label: o };
    if (typeof o === "object" && o !== null) {
      const val = String(o.value ?? o.id ?? o.name ?? o.label ?? "");
      const lbl = String(o.label ?? o.name ?? o.value ?? "");
      return { value: val, label: lbl };
    }
    return { value: String(o ?? ""), label: String(o ?? "") };
  });

  const filtered = normalizedOptions.filter(o =>
    o.label.toLowerCase().includes((search || "").toLowerCase())
  );

  const safeValue = Array.isArray(value) ? value : [];

  const toggle = (optValue: string) => {
    onChange(safeValue.includes(optValue) ? safeValue.filter(v => v !== optValue) : [...safeValue, optValue]);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="w-full min-h-[40px] px-3 py-2 border border-input rounded-md bg-background text-sm text-left flex flex-wrap gap-1.5 items-center hover:border-ring focus:outline-none focus:ring-2 focus:ring-ring/30 transition-colors"
        >
          {safeValue.length === 0 ? (
            <span className="text-muted-foreground">{placeholder}</span>
          ) : (
            safeValue.map(v => {
              const label = normalizedOptions.find(o => o.value === v)?.label || v;
              return (
                <Badge key={v} variant="secondary" className="text-xs rounded-full px-2 py-0.5">
                  {label}
                  <span
                    className="ml-1 cursor-pointer hover:text-destructive"
                    onClick={e => { e.stopPropagation(); toggle(v); }}
                  >&times;</span>
                </Badge>
              );
            })
          )}
          <ChevronDown className="w-4 h-4 ml-auto flex-shrink-0 text-muted-foreground" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[var(--radix-popover-trigger-width)]" align="start">
        <div className="p-2 border-b">
          <Input
            placeholder="Search..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="h-8 text-sm"
            autoFocus
          />
        </div>
        <div className="overflow-auto" style={{ maxHeight }}>
          {filtered.length === 0 ? (
            <div className="p-3 text-center text-sm text-muted-foreground">No matches</div>
          ) : (
            <div className="flex flex-col p-1">
              {filtered.map(o => {
                const isSelected = safeValue.includes(o.value);
                return (
                  <button
                    key={o.value}
                    type="button"
                    onClick={() => toggle(o.value)}
                    className="flex items-center gap-2 px-2 py-1.5 text-sm hover:bg-muted rounded-sm transition-colors text-left"
                  >
                    <div className={`flex-shrink-0 w-4 h-4 rounded-sm border ${isSelected ? 'bg-primary border-primary' : 'border-input'} flex items-center justify-center`}>
                      {isSelected && <Check className="w-3 h-3 text-primary-foreground" />}
                    </div>
                    <span>{o.label}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

/* ─── Step Indicator ─────────────────────────────────────────────────────── */

const STEPS = [
  { label: "Basic Info", icon: User },
  { label: "Demographics", icon: ShieldCheck },
  { label: "Address", icon: Building2 },
  { label: "Contact Info", icon: Phone },
];

function StepIndicator({ current }: { current: number }) {
  return (
    <div className="flex items-center justify-center gap-0 mb-8">
      {STEPS.map((step, i) => {
        const done = i < current;
        const active = i === current;
        const Icon = step.icon;
        return (
          <div key={i} className="flex items-center">
            <div className="flex flex-col items-center gap-1.5">
              <div className={cn(
                "w-9 h-9 rounded-full flex items-center justify-center border-2 transition-all duration-300",
                done ? "bg-primary border-primary text-white"
                  : active ? "bg-white border-primary text-primary shadow-md shadow-primary/20"
                  : "bg-white border-border text-muted-foreground"
              )}>
                {done ? <Check className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
              </div>
              <span className={cn(
                "text-[11px] font-medium whitespace-nowrap hidden sm:block",
                active ? "text-primary" : done ? "text-foreground" : "text-muted-foreground"
              )}>
                {step.label}
              </span>
            </div>
            {i < STEPS.length - 1 && (
              <div className={cn(
                "w-10 sm:w-16 h-0.5 mb-5 transition-all duration-500",
                i < current ? "bg-primary" : "bg-border"
              )} />
            )}
          </div>
        );
      })}
    </div>
  );
}

/* ─── Field helpers ──────────────────────────────────────────────────────── */

function FieldError({ message }: { message?: string }) {
  if (!message) return null;
  return <p className="text-xs text-destructive mt-1">{message}</p>;
}

function FieldLabel({ children, required }: { children: React.ReactNode; required?: boolean }) {
  return (
    <Label className="text-sm font-medium text-foreground">
      {children}{required && <span className="text-destructive ml-0.5">*</span>}
    </Label>
  );
}

/* ─── Main ───────────────────────────────────────────────────────────────── */

export default function MemberRegister() {
  const [, setLocation] = useLocation();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [isDisplayNameManuallyEdited, setIsDisplayNameManuallyEdited] = useState(false);
  const topRef = useRef<HTMLDivElement>(null);

  const { register, control, watch, trigger, setValue, getValues, formState: { errors } } = useForm<FormData>({
    defaultValues: {
      firstName: "", middleName: "", lastName: "", displayName: "",
      ssn: "", gender: "", dob: "", languages: [], localityIds: [],
      email: localStorage.getItem("registerEmail") || "",
      buildingNo: "", street1: "", street2: "", city: "", state: "", zipCode: "",
      phone: "", whatsapp: ""
    },
    mode: "onChange"
  });

  const { data: languagesData = [] } = useGetLanguages();
  const selectedState = watch("state");
  const { data: localitiesData = [] } = useGetLocalities(selectedState);
  const LANGUAGES = languagesData.map((l: any) => ({ value: l.id.toString(), label: l.name }));
  const LOCALITIES = localitiesData.map((loc: any) => ({
    value: loc.id.toString(),
    label: `${loc.name} (${loc.state}${loc.city ? ' - ' + loc.city : ''})`
  }));

  const scrollTop = () => topRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });

  const STEP_FIELDS: (keyof FormData)[][] = [
    ["firstName", "middleName", "lastName", "displayName"],
    ["dob", "ssn", "gender", "languages"],
    ["buildingNo", "street1", "street2", "city", "state", "zipCode", "localityIds"],
    ["phone", "whatsapp"]
  ];

  const watchedValues = watch();

  const isStepValid = () => {
    const fields = STEP_FIELDS[step];
    if (!fields || fields.length === 0) return true;
    
    // Only disable if there are explicit validation errors shown to the user.
    // Otherwise, leave the button enabled so that clicking it triggers RHF validation
    // and displays the appropriate error messages via `trigger()`.
    const hasErrors = fields.some(field => errors[field]);
    return !hasErrors;
  };

  const goNext = async () => {
    const valid = await trigger(STEP_FIELDS[step] as any);
    if (!valid) return;
    setStep(s => Math.min(s + 1, STEPS.length - 1));
    scrollTop();
  };

  const goBack = () => { setStep(s => Math.max(s - 1, 0)); scrollTop(); };

  const onSubmit = async () => {
    const valid = await trigger(STEP_FIELDS[step] as any);
    if (!valid) return;
    setLoading(true);
    try {
      const vals = getValues();
      const res = await fetch(`${API_BASE}/auth/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...vals,
          role: "MEMBER"
        }),
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.error);
      
      if (json.data?.token) {
        localStorage.setItem("token", json.data.token);
        localStorage.setItem("user", JSON.stringify(json.data.user));
      }
      
      localStorage.removeItem("registerEmail");
      
      setLocation("/member/dashboard");
    } catch (err: any) {
      alert(err.message || "Failed to register");
    } finally {
      setLoading(false);
    }
  };

  // SSN formatting (basic XXX-XX-XXXX)
  const formatSSN = (val: string) => {
    const cleaned = val.replace(/\D/g, '').slice(0,9);
    let res = "";
    if (cleaned.length > 0) res += cleaned.slice(0,3);
    if (cleaned.length > 3) res += "-" + cleaned.slice(3,5);
    if (cleaned.length > 5) res += "-" + cleaned.slice(5,9);
    return res;
  };

  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/5 to-secondary/5 p-6">
        <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="text-center max-w-md">
          <div className="w-20 h-20 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center mx-auto mb-6 shadow-lg">
            <Check className="w-10 h-10 text-white" />
          </div>
          <h2 className="text-2xl font-bold mb-2">Registration Submitted!</h2>
          <p className="text-muted-foreground mb-6">
            Your member profile is ready. You can now access personalized care and seamless health coordination.
          </p>
          <Button
            onClick={() => setLocation("/login")}
            className="bg-gradient-to-r from-primary to-secondary text-white rounded-xl h-11 px-8"
          >
            Go to Sign In
          </Button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen grid lg:grid-cols-2" ref={topRef}>
      {/* Sidebar */}
      <div className="hidden lg:flex flex-col bg-gradient-to-br from-primary via-primary/95 to-secondary p-12 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_30%,rgba(255,255,255,0.08),transparent_60%)]" />
        
        <Link href="/" className="flex items-center gap-3 relative z-10 w-fit">
          <div className="w-10 h-10 rounded-2xl bg-white/20 flex items-center justify-center backdrop-blur-md">
            <Heart className="w-5 h-5 text-white" />
          </div>
          <span className="font-bold text-xl tracking-tight">StraightDoc</span>
        </Link>
        
        <div className="flex-1 flex flex-col justify-center relative z-10">
          <Badge className="bg-white/20 text-white hover:bg-white/30 border-0 w-fit mb-4">Member Registration</Badge>
          <motion.h1 
            initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} 
            className="text-4xl font-bold mb-4 leading-tight"
          >
            Take control of your healthcare
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.1 }}
            className="text-white/70 text-lg mb-8"
          >
            Complete your profile to get personalized care, access provider networks, and manage your health seamlessly.
          </motion.p>
          
          <div className="bg-white/10 rounded-2xl p-5 backdrop-blur border border-white/20">
            <p className="text-sm font-medium mb-4">Why join as a Member?</p>
            {[
              "Find the right verified providers",
              "Secure, encrypted health data",
              "Accurate cost estimates upfront",
              "Seamless care coordination",
            ].map((item, i) => (
              <motion.div 
                key={item}
                initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 + i * 0.1 }}
                className="flex items-center gap-3 text-white/90 mb-3 last:mb-0"
              >
                <div className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                  <ShieldCheck className="w-3 h-3 text-white" />
                </div>
                <span className="text-sm">{item}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* Main Form Area */}
      <div className="flex flex-col p-6 sm:p-10 bg-background relative overflow-y-auto">
        <div className="w-full max-w-xl mx-auto relative z-10 my-auto pt-4 pb-12">
          
          {/* Mobile Header */}
          <div className="flex items-center justify-between mb-8 lg:hidden">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
                <Heart className="w-4 h-4 text-white" />
              </div>
              <span className="font-bold text-lg">StraightDoc</span>
            </div>
            <Link href="/login" className="text-sm text-primary font-medium hover:underline">Sign in</Link>
          </div>

          {/* Title */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 bg-primary/10 text-primary text-sm font-medium px-3 py-1.5 rounded-full mb-3">
              <User className="w-4 h-4" />
              Member Registration
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold mb-1">Join the StraightDoc Network</h1>
            <p className="text-muted-foreground">Complete your member profile in {STEPS.length} simple steps</p>
          </div>

          {/* Step Indicator */}
          <StepIndicator current={step} />

          {/* Form Card */}
          <AnimatePresence mode="wait">
            <motion.div
              key={step}
              initial={{ opacity: 0, x: 24 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -24 }}
              transition={{ duration: 0.22 }}
              className="bg-white rounded-2xl border border-border shadow-sm p-6 sm:p-8"
            >
              {/* ── Step 0: Basic Info ── */}
              {step === 0 && (
                <div className="space-y-5">
                  <SectionHeader icon={User} title="Basic Information" subtitle="Tell us about yourself." />
                  
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <FieldLabel required>First Name</FieldLabel>
                      <Input
                        {...register("firstName", { 
                          required: "First name is required",
                          onChange: (e) => {
                            if (!isDisplayNameManuallyEdited) {
                              const vals = getValues();
                              const newFirst = e.target.value;
                              setValue("displayName", `${newFirst} ${vals.lastName}`.trim(), { shouldValidate: true });
                            }
                          }
                        })}
                        placeholder="Jane" className="mt-1.5"
                      />
                      <FieldError message={errors.firstName?.message} />
                    </div>
                    <div>
                      <FieldLabel>Middle Name</FieldLabel>
                      <Input {...register("middleName")} placeholder="M." className="mt-1.5" />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <FieldLabel required>Last Name</FieldLabel>
                      <Input
                        {...register("lastName", { 
                          required: "Last name is required",
                          onChange: (e) => {
                            if (!isDisplayNameManuallyEdited) {
                              const vals = getValues();
                              const newLast = e.target.value;
                              setValue("displayName", `${vals.firstName} ${newLast}`.trim(), { shouldValidate: true });
                            }
                          }
                        })}
                        placeholder="Doe" className="mt-1.5"
                      />
                      <FieldError message={errors.lastName?.message} />
                    </div>
                    <div>
                      <FieldLabel required>Display Name</FieldLabel>
                      <Input
                        {...register("displayName", { 
                          required: "Display name is required",
                          onChange: () => setIsDisplayNameManuallyEdited(true)
                        })}
                        placeholder="Jane Doe" className="mt-1.5"
                      />
                      <FieldError message={errors.displayName?.message} />
                    </div>
                  </div>
                </div>
              )}

              {/* ── Step 1: Demographics ── */}
              {step === 1 && (
                <div className="space-y-5">
                  <SectionHeader icon={ShieldCheck} title="Demographics" subtitle="We need this to verify your identity." />
                  
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <FieldLabel required>Date of Birth</FieldLabel>
                      <Controller
                        name="dob" control={control} rules={{ required: "Date of birth is required" }}
                        render={({ field }) => (
                          <Popover>
                            <PopoverTrigger asChild>
                              <Button
                                variant={"outline"}
                                className={cn(
                                  "w-full justify-start text-left font-normal mt-1.5 h-11",
                                  !field.value && "text-muted-foreground"
                                )}
                              >
                                <CalendarIcon className="mr-2 h-4 w-4" />
                                {field.value ? format(new Date(field.value), "MM/dd/yyyy") : <span>Pick a date</span>}
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0" align="start">
                              <Calendar
                                mode="single"
                                selected={(field.value ? new Date(field.value) : undefined) as any}
                                onSelect={(date: any) => field.onChange(date ? format(date, "yyyy-MM-dd") : "")}
                                captionLayout="dropdown"
                                fromYear={1900}
                                toYear={new Date().getFullYear()}
                                initialFocus
                              />
                            </PopoverContent>
                          </Popover>
                        )}
                      />
                      <FieldError message={errors.dob?.message} />
                    </div>
                    <div>
                      <FieldLabel required>SSN Number</FieldLabel>
                      <Controller
                        name="ssn" control={control} rules={{ required: "SSN is required", minLength: { value: 11, message: "Invalid SSN" } }}
                        render={({ field }) => (
                          <Input 
                            {...field} 
                            placeholder="XXX-XX-XXXX" 
                            className="mt-1.5"
                            onChange={e => field.onChange(formatSSN(e.target.value))}
                            maxLength={11}
                          />
                        )}
                      />
                      <FieldError message={errors.ssn?.message} />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <FieldLabel required>Gender</FieldLabel>
                      <Controller
                        name="gender" control={control} rules={{ required: "Gender is required" }}
                        render={({ field }) => (
                          <Select value={field.value} onValueChange={field.onChange}>
                            <SelectTrigger className="mt-1.5"><SelectValue placeholder="Select gender" /></SelectTrigger>
                            <SelectContent>{GENDERS.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
                          </Select>
                        )}
                      />
                      <FieldError message={errors.gender?.message} />
                    </div>
                    <div>
                      <FieldLabel>Languages Spoken</FieldLabel>
                      <div className="mt-1.5">
                        <Controller
                          name="languages" control={control}
                          render={({ field }) => (
                            <MultiSelect options={LANGUAGES} value={field.value} onChange={field.onChange} placeholder="Select languages" />
                          )}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* ── Step 2: Address ── */}
              {step === 2 && (
                <div className="space-y-5">
                  <SectionHeader icon={Building2} title="Address" subtitle="Your residential address details." />
                  
                  <div className="grid sm:grid-cols-3 gap-4">
                    <div>
                      <FieldLabel>Building No.</FieldLabel>
                      <Input {...register("buildingNo")} placeholder="123A" className="mt-1.5" />
                    </div>
                    <div className="sm:col-span-2">
                      <FieldLabel required>Street Name 1</FieldLabel>
                      <Input
                        {...register("street1", { required: "Street name is required" })}
                        placeholder="Main St" className="mt-1.5"
                      />
                      <FieldError message={errors.street1?.message} />
                    </div>
                  </div>

                  <div>
                    <FieldLabel>Street Name 2</FieldLabel>
                    <Input {...register("street2")} placeholder="Apt 4B" className="mt-1.5" />
                  </div>

                  <div className="grid sm:grid-cols-3 gap-4">
                    <div>
                      <FieldLabel required>City</FieldLabel>
                      <Input
                        {...register("city", { required: "City is required" })}
                        placeholder="New York" className="mt-1.5"
                      />
                      <FieldError message={errors.city?.message} />
                    </div>
                    <div>
                      <FieldLabel required>State</FieldLabel>
                      <Controller
                        name="state" control={control} rules={{ required: "State is required" }}
                        render={({ field }) => (
                          <Select value={field.value} onValueChange={field.onChange}>
                            <SelectTrigger className="mt-1.5"><SelectValue placeholder="State" /></SelectTrigger>
                            <SelectContent className="max-h-60">
                              {US_STATES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        )}
                      />
                      <FieldError message={errors.state?.message} />
                    </div>
                    <div>
                      <FieldLabel required>Zip Code</FieldLabel>
                      <Input
                        {...register("zipCode", { required: "Zip Code is required" })}
                        placeholder="10001" className="mt-1.5"
                      />
                      <FieldError message={errors.zipCode?.message} />
                    </div>
                  </div>

                  <div>
                    <FieldLabel required>Primary Locality / Area</FieldLabel>
                    <p className="text-xs text-muted-foreground mt-0.5 mb-1.5">
                      {selectedState ? `Showing localities for ${selectedState}` : "Select a state above to filter localities"}
                    </p>
                    <div className="mt-1.5">
                      <Controller
                        name="localityIds" control={control}
                        rules={{ required: "At least one locality is mandatory" }}
                        render={({ field }) => (
                          <MultiSelect
                            options={LOCALITIES}
                            value={field.value}
                            onChange={field.onChange}
                            placeholder={selectedState ? `Select localities in ${selectedState}...` : "Select localities..."}
                          />
                        )}
                      />
                    </div>
                    <FieldError message={errors.localityIds?.message} />
                  </div>
                </div>
              )}

              {/* ── Step 3: Contact Info ── */}
              {step === 3 && (
                <div className="space-y-5">
                  <SectionHeader icon={Phone} title="Contact Information" subtitle="How can we reach you?" />
                  
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <FieldLabel required>Phone Number</FieldLabel>
                      <Input
                        {...register("phone", { 
                          required: "Phone number is required",
                          pattern: { value: /^[^a-zA-Z]+$/, message: "Alphabets are not allowed" }
                        })}
                        onInput={(e) => { e.currentTarget.value = e.currentTarget.value.replace(/[a-zA-Z]/g, '') }}
                        type="tel" placeholder="(555) 123-4567" className="mt-1.5"
                      />
                      <FieldError message={errors.phone?.message} />
                    </div>
                    <div>
                      <FieldLabel>WhatsApp Number</FieldLabel>
                      <Input 
                        {...register("whatsapp", {
                          pattern: { value: /^[^a-zA-Z]*$/, message: "Alphabets are not allowed" }
                        })}
                        onInput={(e) => { e.currentTarget.value = e.currentTarget.value.replace(/[a-zA-Z]/g, '') }}
                        type="tel" placeholder="(555) 123-4567" className="mt-1.5" 
                      />
                    </div>
                  </div>
                </div>
              )}
            </motion.div>
          </AnimatePresence>

          {/* Navigation */}
          <div className="flex items-center justify-between mt-6">
            <div className="text-xs text-muted-foreground">Step {step + 1} of {STEPS.length}</div>

            <div className="flex items-center gap-3">
              <Button
                type="button" variant="outline"
                className="rounded-xl h-11 px-6"
                onClick={step === 0 ? () => setLocation("/login") : goBack}
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                {step === 0 ? "Back to Login" : "Back"}
              </Button>

              {step < STEPS.length - 1 ? (
                <Button
                  type="button"
                  className="rounded-xl h-11 px-6 bg-gradient-to-r from-primary to-secondary text-white"
                  onClick={goNext}
                  disabled={!isStepValid()}
                >
                  Continue <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              ) : (
                <Button
                  type="button"
                  className="rounded-xl h-11 px-6 bg-gradient-to-r from-primary to-secondary text-white"
                  onClick={onSubmit}
                  disabled={loading || !isStepValid()}
                >
                  {loading ? "Submitting…" : <><Check className="w-4 h-4 mr-1.5" />Submit Registration</>}
                </Button>
              )}
            </div>
          </div>
          
        </div>
      </div>
    </div>
  );
}

/* ─── SectionHeader ──────────────────────────────────────────────────────── */

function SectionHeader({ icon: Icon, title, subtitle }: { icon: React.ElementType; title: string; subtitle: string }) {
  return (
    <div className="flex items-start gap-3 pb-4 border-b border-border">
      <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
        <Icon className="w-5 h-5 text-primary" />
      </div>
      <div>
        <h2 className="font-semibold text-lg">{title}</h2>
        <p className="text-sm text-muted-foreground">{subtitle}</p>
      </div>
    </div>
  );
}
